import { useSeo } from "../hooks/useSeo";
import { PageHeader } from "../components/PageHeader";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { PillButton } from "../components/PillButton";
import { ESPERIENZE, PUBBLICAZIONI, DOCENZE } from "../data/content";
import { ArrowRight, Briefcase, BookOpen } from "lucide-react";

export default function Esperienze() {
  useSeo(
    "Esperienze professionali e pubblicazioni — Susanna Paoletti",
    "Le esperienze professionali di Susanna Paoletti, psicologa e psicoterapeuta a Macerata: Centro Sclerosi Multipla, attività libero-professionale e pubblicazioni scientifiche."
  );

  return (
    <>
      <PageHeader
        eyebrow="Esperienze professionali"
        title="Oltre vent'anni di esperienza clinica e professionale"
        subtitle="Dall'esperienza in ambito ospedaliero all'attività psicoterapeutica individuale e di coppia."
      />

      {/* ESPERIENZE PROFESSIONALI */}
      <section className="py-20 md:py-28" data-testid="esperienze">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 text-sage-dark mb-8">
            <Briefcase size={22} />
            <span className="text-xs uppercase tracking-[0.28em]">Il percorso</span>
          </div>

          <div className="grid md:grid-cols-12 gap-8 lg:gap-12">
            <div className="md:col-span-8 space-y-6">
              {ESPERIENZE.map((p, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className="text-lg text-stone leading-relaxed">
                    {p}
                  </p>
                </Reveal>
              ))}
            </div>
            <div className="md:col-span-4">
              <Reveal delay={0.1}>
                <div className="bg-sand border border-border rounded-2xl p-7 space-y-6 md:sticky md:top-28">
                  <div>
                    <p className="font-serif text-4xl text-sage-dark">2003 – 2016</p>
                    <p className="text-stone text-sm mt-2">Centro Sclerosi Multipla, U.O. Neurologia — Ospedale di Macerata. Valutazioni neuropsicologiche e sostegno psicologico; terapia di gruppo per l'AISM.</p>
                  </div>
                  <div className="h-px bg-border" />
                  <div>
                    <p className="font-serif text-4xl text-sage-dark">dal 2005</p>
                    <p className="text-stone text-sm mt-2">Attività libero-professionale presso il mio studio: psicoterapia individuale e terapia di coppia.</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* PUBBLICAZIONI */}
      <section className="py-20 md:py-28 bg-ink text-cream relative overflow-hidden" data-testid="pubblicazioni">
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="flex items-center gap-3 text-sage-light mb-6">
            <BookOpen size={22} />
            <span className="text-xs uppercase tracking-[0.28em]">Pubblicazioni e docenze</span>
          </div>
          <Reveal delay={0.05}>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-tight mb-14 max-w-2xl">
              Contributi e ricerca
            </h2>
          </Reveal>

          <StaggerGroup className="border-t border-cream/15">
            {PUBBLICAZIONI.map((p, i) => (
              <StaggerItem key={i}>
                <article className="grid md:grid-cols-12 gap-3 md:gap-8 py-8 border-b border-cream/15" data-testid={`pubblicazione-${i}`}>
                  <div className="md:col-span-3">
                    <span className="font-serif text-3xl text-sage-light">{p.year}</span>
                  </div>
                  <div className="md:col-span-9">
                    <p className="text-cream/75 leading-relaxed">{p.text}</p>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <h3 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight mt-20 mb-12">
            Docenze
          </h3>
          <StaggerGroup className="border-t border-cream/15">
            {DOCENZE.map((d, i) => (
              <StaggerItem key={i}>
                <article className="grid md:grid-cols-12 gap-3 md:gap-8 py-8 border-b border-cream/15" data-testid={`docenza-${i}`}>
                  <div className="md:col-span-3">
                    <span className="font-serif text-3xl text-sage-light">{d.year}</span>
                  </div>
                  <div className="md:col-span-9">
                    <p className="text-cream/75 leading-relaxed">{d.text}</p>
                  </div>
                </article>
              </StaggerItem>
            ))}
          </StaggerGroup>

          <Reveal>
            <div className="mt-14 flex flex-col sm:flex-row gap-4">
              <PillButton to="/contatti" testid="esperienze-cta">
                Richiedi un primo colloquio <ArrowRight size={18} />
              </PillButton>
              <PillButton to="/formazione" variant="outline" className="border-cream/30 text-cream hover:bg-cream hover:text-ink" testid="esperienze-formazione">
                Vai alla Formazione
              </PillButton>
            </div>
          </Reveal>
        </div>
        <div className="pointer-events-none absolute -bottom-32 -left-20 w-[380px] h-[380px] rounded-full bg-sage/20 blur-3xl" />
      </section>
    </>
  );
}
